import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getCredentialPanelStyles } from "../../../theme/panelStyles";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { plentyCredentialsApi } from "../../../services/api/plenty/plenty_credentials";
const PlentyCredentials = () => {
  const theme = useTheme();
  const cp = getCredentialPanelStyles(theme);

  const getErrorMessage = (error) => {
    if (typeof error === "string") return error;

    // Handle axios error structure
    if (error.response?.data?.detail) {
      const detail = error.response.data.detail;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail)) {
        // Handle validation errors array
        return detail
          .map((err) =>
            typeof err === "string"
              ? err
              : err.msg
                ? `${err.loc?.join(".")} ${err.msg}`
                : JSON.stringify(err),
          )
          .join("; ");
      }
      return JSON.stringify(detail);
    }

    if (error.response?.data?.message) {
      return typeof error.response.data.message === "string"
        ? error.response.data.message
        : JSON.stringify(error.response.data.message);
    }

    if (error.message) {
      return typeof error.message === "string"
        ? error.message
        : JSON.stringify(error.message);
    }

    return JSON.stringify(error);
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  //Plenty
  const [PlentyCredentials, setPlentyCredentials] = useState(() => {
    try {
      const stored = localStorage.getItem("plentyCredentials");
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [PlentyCredentialDialog, setPlentyCredentialDialog] = useState(false);
  const [PlentyEditingCredential, setPlentyEditingCredential] = useState(null);
  const [PlentyForm, setPlentyForm] = useState({
    id: "",
    username: "",
    password: "",
    plenty_id: "",
  });
  const [formTestStatus, setFormTestStatus] = useState("");

  useEffect(() => {
    // Load initial data with error handling
    const initializeData = async () => {
      try {
        // Load all data in parallel but handle each error separately
        const loadOperations = [
          loadPlentyCredentials().catch((error) => {
            console.error("Failed to load Plenty credentials on init:", error);
            return null; // Continue with other operations
          }),
        ];

        await Promise.allSettled(loadOperations);
      } catch (error) {
        console.error("Unexpected error during initialization:", error);
        setError(getErrorMessage(error));
      }
    };

    initializeData();
  }, []);

  useEffect(() => {
    if (snackbarOpen) {
      const timer = setTimeout(() => setSnackbarOpen(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [snackbarOpen]);

  const handleEditPlentyCredential = (credential) => {
    setPlentyEditingCredential(credential);
    setPlentyForm({
      id: credential.id || "",
      username: credential.username || "",
      password: credential.password || "",
      plenty_id: credential.plenty_id || "",
    });
    setFormTestStatus(""); // Clear test status
    setPlentyCredentialDialog(true);
  };

  const loadPlentyCredentials = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await plentyCredentialsApi.getCredentials();
      const credentials = response.data || [];

      // Test each credential and update status
      for (const cred of credentials) {
        try {
          const testResponse = await plentyCredentialsApi.testCredential({
            username: cred.username,
            password: cred.password,
            plenty_id: cred.plenty_id,
          });
          const testResult = testResponse.data || {};
          cred.status = testResult.valid ? "Connected" : "Disconnected";
        } catch (error) {
          console.error(
            `Failed to test credential for ${cred.username}:`,
            error,
          );
          cred.status = "Disconnected";
        }
      }

      setPlentyCredentials(credentials);
      localStorage.setItem("plentyCredentials", JSON.stringify(credentials));
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to load Plenty credentials:", error);
      setError(getErrorMessage(error));
      // Set empty array on error to show "no credentials" message
      setPlentyCredentials([]);
      localStorage.setItem("plentyCredentials", JSON.stringify([]));
    } finally {
      setLoading(false);
    }
  };

  const createPlentyCredential = async () => {
    if (!PlentyForm.username || !PlentyForm.password || !PlentyForm.plenty_id) {
      setError("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await plentyCredentialsApi.createCredential(PlentyForm);
      const newCredential = response.data || {};

      // Test the newly created credential and set status
      try {
        const testResponse = await plentyCredentialsApi.testCredential({
          username: newCredential.username,
          password: newCredential.password,
          plenty_id: newCredential.plenty_id,
        });
        const testResult = testResponse.data || {};
        newCredential.status = testResult.valid ? "Connected" : "Disconnected";
      } catch (testError) {
        console.error("Failed to test new credential:", testError);
        newCredential.status = "Disconnected";
      }

      setPlentyCredentials((prev) => [...prev, newCredential]);

      setSuccess("Plenty credential created successfully!");
      setSnackbarOpen(true);
      setPlentyCredentialDialog(false);
      setPlentyEditingCredential(null);
      setPlentyForm({
        id: "",
        username: "",
        password: "",
        plenty_id: "",
      });
      setFormTestStatus("");
    } catch (error) {
      console.error("Failed to create Plenty credential:", error);
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const updatePlentyCredential = async () => {
    if (!PlentyForm.username || !PlentyForm.password || !PlentyForm.plenty_id) {
      setError("Please fill in all required fields");
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = {
        username: PlentyForm.username,
        password: PlentyForm.password,
        plenty_id: PlentyForm.plenty_id,
      };

      const response = await plentyCredentialsApi.updateCredential(
        PlentyForm.id,
        data,
      );
      const updatedCredential = response.data || {};

      // Test the updated credential and set status
      try {
        const testResponse = await plentyCredentialsApi.testCredential({
          username: updatedCredential.username,
          password: updatedCredential.password,
          plenty_id: updatedCredential.plenty_id,
        });
        const testResult = testResponse.data || {};
        updatedCredential.status = testResult.valid
          ? "Connected"
          : "Disconnected";
      } catch (testError) {
        console.error("Failed to test updated credential:", testError);
        updatedCredential.status = "Disconnected";
      }

      setPlentyCredentials((prev) =>
        prev.map((cred) =>
          cred.id === updatedCredential.id ? updatedCredential : cred,
        ),
      );

      setSuccess("Plenty credential updated successfully!");
      setSnackbarOpen(true);
      setPlentyEditingCredential(null);
      setPlentyForm({
        id: "",
        username: "",
        password: "",
        plenty_id: "",
      });
      setPlentyCredentialDialog(false);
      setFormTestStatus("");
    } catch (error) {
      console.error("Failed to update Plenty credential:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to update Plenty credential",
      );
    } finally {
      setLoading(false);
    }
  };

  const deletePlentyCredential = async (credentialId) => {
    try {
      setLoading(true);
      setError(null);

      await plentyCredentialsApi.deleteCredential(credentialId);
      setPlentyCredentials((prev) =>
        prev.filter((cred) => cred.id !== credentialId),
      );

      setSuccess("Plenty credential deleted successfully!");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to delete Plenty credential:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to delete Plenty credential",
      );
    } finally {
      setLoading(false);
    }
  };

  const testPlentyCredential = async (username, password, plenty_id) => {
    if (!username || !password || !plenty_id) {
      setError("Please fill in all required fields to test the credential");
      return false;
    }
    try {
      setLoading(true);
      setError(null);

      const response = await plentyCredentialsApi.testCredential({
        username,
        password,
        plenty_id,
      });
      const testResult = response.data || {};

      if (testResult.valid) {
        setSuccess("Plenty credential tested successfully!");
        return true;
      } else {
        setError("Plenty credential test failed - invalid credentials");
        return false;
      }
    } catch (error) {
      console.error("Failed to test Plenty credential:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to test Plenty credential",
      );
      return false;
    } finally {
      setLoading(false);
    }
  };
  // Function for testing a credential from the list and updating its status
  const testAndUpdateCredentialStatus = async (
    credentialId,
    username,
    password,
    plenty_id,
  ) => {
    try {
      setLoading(true);
      setError(null);

      const response = await plentyCredentialsApi.testCredential({
        username,
        password,
        plenty_id,
      });
      const testResult = response.data || {};

      const newStatus = testResult.valid ? "Connected" : "Disconnected";

      // Update the credential status in the state
      setPlentyCredentials((prev) =>
        prev.map((cred) =>
          cred.id === credentialId ? { ...cred, status: newStatus } : cred,
        ),
      );

      if (testResult.valid) {
        setSuccess("Plenty credential tested successfully - Connected!");
      } else {
        setError("Plenty credential test failed - Disconnected");
      }
    } catch (error) {
      console.error("Failed to test Plenty credential:", error);

      // Update status to Disconnected on error
      setPlentyCredentials((prev) =>
        prev.map((cred) =>
          cred.id === credentialId ? { ...cred, status: "Disconnected" } : cred,
        ),
      );

      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to test Plenty credential",
      );
    } finally {
      setLoading(false);
    }
  };
  const testPlentyCredentialInForm = async (username, password, plenty_id) => {
    if (!username || !password || !plenty_id) {
      setFormTestStatus(
        "❌ Please fill in all required fields to test the credential",
      );
      return;
    }
    try {
      setFormTestStatus("🔄 Testing credentials...");

      const response = await plentyCredentialsApi.testCredential({
        username,
        password,
        plenty_id,
      });
      const testResult = response.data || {};

      if (testResult.valid) {
        setFormTestStatus("✅ Credentials are valid and connected!");
      } else {
        setFormTestStatus("❌ Credentials test failed - invalid credentials");
      }
    } catch (error) {
      console.error("Failed to test Plenty credential:", error);
      setFormTestStatus(
        "❌ " +
          (error.response?.data?.detail ||
            error.message ||
            "Failed to test credential"),
      );
    }
  };

  const togglePlentyCredentialActive = async (credentialId) => {
    try {
      setLoading(true);
      setError(null);

      const response =
        await plentyCredentialsApi.toggleActiveCredential(credentialId);
      setPlentyCredentials((prev) =>
        prev.map((cred) =>
          cred.id === credentialId ? { ...cred, active: !cred.active } : cred,
        ),
      );

      setSuccess("Plenty credential toggled successfully!");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to toggle Plenty credential:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to toggle Plenty credential",
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePlentyFormChange = (field, value) => {
    setPlentyForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card sx={cp.outerCard}>
          <CardContent sx={{ p: 4 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={4}
              sx={{
                ...cp.header,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  marginRight: "auto",
                  paddingRight: 2,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                  🏪 Plenty API Credentials
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Manage your Plenty API access credentials
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2, marginLeft: "auto" }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadPlentyCredentials}
                  disabled={loading}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  Reload
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setPlentyCredentialDialog(true)}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  Add Credentials
                </Button>
              </Box>
            </Box>

            <Alert
              severity="info"
              sx={cp.infoAlert}
              icon={<CheckCircleIcon sx={{ color: "info.main" }} />}
            >
              <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                📋 How to Get Your Plenty Credentials:
              </Typography>
              <Box component="ol" sx={{ m: 0, pl: 3 }}>
                <li>Use your Plenty account credentials</li>
                <li>Enter the credentials in the form below</li>
              </Box>
              <Typography variant="body2" sx={{ mt: 2, fontStyle: "italic" }}>
                For more information, visit{" "}
                <a
                  href="https://account.plentysystems.com/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#1976d2",
                    textDecoration: "underline",
                    fontWeight: "bold",
                  }}
                >
                  Plenty Login
                </a>
              </Typography>
            </Alert>

            {PlentyCredentials.length === 0 ? (
              <Box sx={cp.emptyState}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: "text.secondary" }}
                >
                  No Credentials Found
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 3, color: "text.secondary" }}
                >
                  Add your first Plenty credential to get started
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setPlentyCredentialDialog(true)}
                  sx={{
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    ...cp.ctaButton,
                  }}
                >
                  Add Your First Credential
                </Button>
              </Box>
            ) : (
              <List
                sx={{
                  width: "100%",
                  border: "2px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  boxShadow: 2,
                }}
              >
                {PlentyCredentials.map((credential, index) => (
                  <ListItem
                    key={credential.id}
                    divider={index < PlentyCredentials.length - 1}
                    sx={{
                      px: 4,
                      py: 3,
                      borderBottom:
                        index < PlentyCredentials.length - 1
                          ? "2px solid"
                          : "none",
                      borderColor: "divider",
                      "&:hover": {
                        backgroundColor: "action.hover",
                        transform: "translateX(4px)",
                      },
                      transition: "all 0.2s ease",
                      borderRadius:
                        index === 0
                          ? "8px 8px 0 0"
                          : index === PlentyCredentials.length - 1
                            ? "0 0 8px 8px"
                            : 0,
                    }}
                  >
                    <Grid container alignItems="center" spacing={3}>
                      <Grid item xs={12} md={8}>
                        <Typography variant="h6">
                          {credential.username}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            wordBreak: "break-all",
                            pr: 2,
                          }}
                        >
                          Username: {credential.username}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            wordBreak: "break-all",
                            pr: 2,
                          }}
                        >
                          Plenty ID: {credential.plenty_id}
                        </Typography>
                        <Box
                          sx={{
                            mt: 0.5,
                            display: "flex",
                            gap: 1,
                            alignItems: "center",
                          }}
                        >
                          <Chip
                            label={credential.status}
                            size="small"
                            color={
                              credential.status === "Connected"
                                ? "success"
                                : "error"
                            }
                          />
                        </Box>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        md={4}
                        sx={{
                          display: "flex",
                          justifyContent: { xs: "center", md: "flex-end" },
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        {/* Active Toggle Switch */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            mr: 2,
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Switch
                                checked={credential.active}
                                onChange={() =>
                                  togglePlentyCredentialActive(credential.id)
                                }
                                disabled={credential.status !== "Connected"}
                                size="small"
                                sx={{
                                  "& .MuiSwitch-switchBase.Mui-checked": {
                                    color: "success.main",
                                  },
                                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                    {
                                      backgroundColor: "success.main",
                                    },
                                }}
                              />
                            }
                            label=""
                            sx={{ m: 0 }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: credential.active
                                ? "success.main"
                                : "text.secondary",
                              fontWeight: "bold",
                              mt: 0.5,
                            }}
                          >
                            {credential.active ? "Active" : "Inactive"}
                          </Typography>
                        </Box>

                        <IconButton
                          onClick={() =>
                            testAndUpdateCredentialStatus(
                              credential.id,
                              credential.username,
                              credential.password,
                              credential.plenty_id,
                            )
                          }
                          disabled={
                            loading ||
                            !credential.username ||
                            !credential.password ||
                            !credential.plenty_id
                          }
                          color="info"
                          title="Test Credentials"
                          sx={{
                            backgroundColor: "info.main",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "info.dark",
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          {loading ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CheckCircleIcon />
                          )}
                        </IconButton>
                        <IconButton
                          title="Edit Credential"
                          onClick={() => handleEditPlentyCredential(credential)}
                          color="warning"
                          sx={{
                            backgroundColor: "warning.main",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "warning.dark",
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => deletePlentyCredential(credential.id)}
                          title="Delete Credential"
                          sx={{
                            backgroundColor: "error.main",
                            color: "white",
                            "&:hover": {
                              backgroundColor: "error.dark",
                              transform: "scale(1.1)",
                            },
                            transition: "all 0.2s ease",
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Grid>
      {/* Plenty Credential Dialog */}
      <Dialog
        open={PlentyCredentialDialog}
        onClose={() => {
          setPlentyCredentialDialog(false);
          setPlentyEditingCredential(null);
          setFormTestStatus("");
        }}
        maxWidth="sm"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
            ...cp.dialogPaper,
          },
        }}
      >
        <DialogTitle
          sx={{
            ...cp.dialogHeader,
            color: "white",
            textAlign: "center",
            py: 3,
            fontSize: "1.5rem",
            fontWeight: "bold",
            borderRadius: "12px 12px 0 0",
          }}
        >
          {PlentyEditingCredential
            ? "✏️ Edit Plenty Credentials"
            : "➕ Add Plenty Credentials"}
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="UserName"
                value={PlentyForm.username}
                onChange={(e) =>
                  setPlentyForm((prev) => ({
                    ...prev,
                    username: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={PlentyForm.password}
                onChange={(e) =>
                  setPlentyForm((prev) => ({
                    ...prev,
                    password: e.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Plenty ID"
                value={PlentyForm.plenty_id}
                onChange={(e) =>
                  setPlentyForm((prev) => ({
                    ...prev,
                    plenty_id: e.target.value,
                  }))
                }
              />
            </Grid>
            {/* Add test status display */}
            {formTestStatus && (
              <Grid item xs={12}>
                <Alert
                  severity={
                    formTestStatus.includes("✅")
                      ? "success"
                      : formTestStatus.includes("🔄")
                        ? "info"
                        : "error"
                  }
                  sx={{ mt: 1 }}
                >
                  {formTestStatus}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            p: 3,
            pt: 0,
            justifyContent: "space-between",
            backgroundColor: "grey.50",
            borderRadius: "0 0 12px 12px",
          }}
        >
          <Button
            onClick={() => {
              setPlentyCredentialDialog(false);
              setPlentyEditingCredential(null);
              setFormTestStatus("");
              setPlentyForm({
                id: "",
                username: "",
                password: "",
                plenty_id: "",
              });
            }}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              "&:hover": {
                backgroundColor: "grey.100",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              testPlentyCredentialInForm(
                PlentyForm.username,
                PlentyForm.password,
                PlentyForm.plenty_id,
              )
            }
            variant="outlined"
            disabled={
              loading ||
              !PlentyForm.username ||
              !PlentyForm.password ||
              !PlentyForm.plenty_id
            }
            sx={{
              borderRadius: 2,
              px: 3,
              "&:hover": {
                backgroundColor: "grey.100",
              },
            }}
          >
            {loading ? <CircularProgress size={20} /> : "Test Credentials"}
          </Button>
          <Button
            onClick={
              PlentyEditingCredential
                ? updatePlentyCredential
                : createPlentyCredential
            }
            variant="contained"
            disabled={
              loading ||
              !PlentyForm.username ||
              !PlentyForm.password ||
              !PlentyForm.plenty_id
            }
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              ...cp.ctaButton,
              "&:hover": {
                background: "linear-gradient(45deg, #1976D2 30%, #1CB5E0 90%)",
                transform: "translateY(-2px)",
              },
              "&:disabled": {
                background: "grey.400",
                color: "white",
              },
              transition: "all 0.3s ease",
            }}
          >
            {loading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Processing...
              </Box>
            ) : PlentyEditingCredential ? (
              "Update Credential"
            ) : (
              "Create Credential"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default PlentyCredentials;
